import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { ResourceCard, SelectionBar, LanguageBar } from '../components/ResourceComponents';
import { ShareListButton } from '../components/ShareListButton';
import OnboardingTour from '../components/OnboardingTour';
import { 
  Search, Plus, Filter, Download, Upload, LogOut, X, Loader2,
  Heart, Printer, Settings, CheckSquare, Square, FlaskConical, HelpCircle
} from 'lucide-react';
import Papa from 'papaparse';
import { SUPPORTED_LANGUAGES } from '../lib/i18n';
import { ReachLoader } from '../components/ReachLoader';
import { usePageTitle } from '../lib/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const PUBLIC_URL = process.env.REACT_APP_BACKEND_URL;

const SDOH_CATEGORIES = [
  "Food Security", "Housing & Shelter", "Utilities", "Healthcare Access",
  "Mental Health", "Substance Use", "Legal / Immigration", "Employment / Income",
  "Transportation", "Education / ESL", "Childcare / Family Support",
  "Violence / Safety", "Digital Access"
];

const COST_OPTIONS = ["Free", "Sliding scale", "Insurance required", "Fee-based"];
const SERVICE_MODALITIES = ["In-person", "Phone", "Virtual", "Mobile / Outreach"];
const FILTER_LANGUAGES = [
  "English", "Spanish", "Haitian Creole", "Portuguese", "Cape Verdean Creole",
  "Arabic", "French", "Chinese", "Vietnamese"
];

const EMPTY_FILTERS = { sdoh_categories: [], neighborhoods: [], languages: [], costs: [], modalities: [] };

const FindResourcesPage = () => {
  usePageTitle('Find Resources');
  const navigate = useNavigate();
  const { getAuthHeaders, logout, user } = useAuth();
  const [loadError, setLoadError] = useState(false);
  
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedResources, setSelectedResources] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [testMode, setTestMode] = useState(false);
  const [showTour, setShowTour] = useState(false);

  // Fetch test mode from server (universal for all users)
  useEffect(() => {
    const fetchTestMode = async () => {
      try {
        const response = await axios.get(`${API_URL}/settings/test-mode`, { headers: getAuthHeaders() });
        setTestMode(response.data.enabled || false);
      } catch (error) {
        console.error('Test mode check failed:', error.message);
      }
    };
    fetchTestMode();
    const interval = setInterval(fetchTestMode, 30000);
    return () => clearInterval(interval);
  }, [getAuthHeaders]);

  // Fetch neighborhoods on mount
  useEffect(() => {
    const fetchNeighborhoods = async () => {
      try {
        const response = await axios.get(`${API_URL}/public/neighborhoods`);
        setNeighborhoods(response.data.neighborhoods || []);
      } catch (error) {
        console.error('Failed to fetch neighborhoods:', error.message);
        setNeighborhoods(['Roxbury', 'Dorchester', 'Mattapan', 'East Boston', 'Other']);
      }
    };
    fetchNeighborhoods();
  }, []);

  // Auto-open guided tour for first-time users
  useEffect(() => {
    if (!localStorage.getItem('resourcenav_tour_seen')) {
      const timer = setTimeout(() => {
        setShowTour(true);
        localStorage.setItem('resourcenav_tour_seen', '1');
      }, 700);
      return () => clearTimeout(timer);
    }
  }, []);

  const fetchResources = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filters.sdoh_categories.length) params.append('sdoh_categories', filters.sdoh_categories.join(','));
      if (filters.neighborhoods.length) params.append('neighborhood', filters.neighborhoods.join(','));
      if (filters.languages.length) params.append('language', filters.languages.join(','));
      if (filters.costs.length) params.append('cost', filters.costs.join(','));
      if (filters.modalities.length) params.append('modality', filters.modalities.join(','));
      
      const response = await axios.get(`${API_URL}/resources?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      setResources(response.data);
      setLoadError(false);
    } catch (error) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filters, getAuthHeaders]);

  useEffect(() => {
    const debounceTimer = setTimeout(fetchResources, 300);
    return () => clearTimeout(debounceTimer);
  }, [fetchResources]);

  // Staff page stays in English (fast/cheap); language selection only carries
  // through to the client-facing Resource List, where translation actually runs.
  const handleLanguageChange = useCallback((lang) => {
    setCurrentLanguage(lang);
  }, []);

  const getDisplayText = useCallback((resource, field) => resource[field] || '', []);
  const getResourceName = useCallback((resource) => <span>{resource.resource_name || ''}</span>, []);

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API_URL}/resources/export/csv`, {
        headers: getAuthHeaders()
      });
      const csv = Papa.unparse(response.data);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `resources_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      toast.success('CSV exported successfully!');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearchQuery('');
    setSelectedResources([]);
    setSelectionMode(false);
  };

  const toggleArrayValue = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter(v => v !== value) : [...prev[key], value]
    }));
  };

  const toggleResourceSelection = (resourceId) => {
    setSelectedResources(prev => 
      prev.includes(resourceId) ? prev.filter(id => id !== resourceId) : [...prev, resourceId]
    );
  };

  const buildListParams = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedResources.length > 0) {
      params.append('ids', selectedResources.join(','));
    } else {
      if (filters.sdoh_categories.length) params.append('categories', filters.sdoh_categories.join(','));
      if (filters.neighborhoods.length) params.append('neighborhood', filters.neighborhoods.join(','));
      if (filters.costs.length) params.append('cost', filters.costs.join(','));
    }
    if (currentLanguage !== 'en') params.append('lang', currentLanguage);
    return params;
  }, [selectedResources, filters, currentLanguage]);

  const trackHandout = useCallback(async () => {
    if (testMode) return;
    const resourceIds = selectedResources.length > 0 ? selectedResources : resources.map(r => r.id);
    try {
      await axios.post(`${API_URL}/analytics/track-handout`, {
        resource_ids: resourceIds, categories: filters.sdoh_categories, language: currentLanguage
      }, { headers: getAuthHeaders() });
    } catch (error) {
      console.error('Analytics tracking failed:', error.message);
    }
  }, [testMode, selectedResources, resources, filters.sdoh_categories, currentLanguage, getAuthHeaders]);

  const handleCreateList = async () => {
    await trackHandout();
    navigate(`/print?${buildListParams().toString()}`);
  };

  const shareUrl = `${PUBLIC_URL}/share?${buildListParams().toString()}`;

  const activeFilterCount = filters.sdoh_categories.length + filters.neighborhoods.length +
    filters.languages.length + filters.costs.length + filters.modalities.length;

  const freeLabel = 'Free';

  const FilterChip = ({ label, onRemove, testid }) => (
    <Badge variant="secondary" data-testid={testid}
      className="bg-[#5C4023]/10 text-[#5C4023] rounded-full px-3 py-1 border border-[#5C4023]/20 font-medium">
      {label}
      <X className="w-3 h-3 ml-2 cursor-pointer hover:text-[#E07A5F]" onClick={onRemove} />
    </Badge>
  );

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Test Mode Banner */}
      {testMode && (
        <div className="bg-amber-500 text-white text-center py-2 px-4 text-sm font-medium">
          <FlaskConical className="w-4 h-4 inline mr-2" />
          Test Mode Active - Resource Lists will NOT be tracked in analytics
        </div>
      )}
      
      {/* Header */}
      <header className="sticky-header border-b border-[#E0D6C8] relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none opacity-[0.32] dark:opacity-[0.05]"
          style={{ backgroundImage: "url('/skyline-band.jpg')", backgroundSize: 'cover', backgroundPosition: 'center bottom' }}
        />
        <div className="max-w-5xl mx-auto px-4 py-4 relative">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <div data-testid="app-logo" className="py-0.5">
                  <img src="/logo-horizontal.png" alt="617Reach — Resources. In Reach. In Boston. For You." className="h-11 w-auto dark:hidden" />
                  <img src="/logo-horizontal-dark.png" alt="617Reach — Resources. In Reach. In Boston. For You." className="h-11 w-auto hidden dark:block" />
                </div>
                <p className="text-xs text-[#888888]">Resource Hub <span data-testid="build-marker" className="text-[10px] text-[#BCBCBC]">v{new Date('2026-06-01T12:00:00Z').toLocaleDateString()}</span></p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button onClick={() => setShowTour(true)} variant="ghost" size="sm"
                data-testid="help-tour-btn" className="text-[#5A5A5A] hover:text-[#5C4023] hover:bg-[#F3EFE9]"
                title="How to use this app" aria-label="How to use this app">
                <HelpCircle className="w-4 h-4" />
              </Button>
              {user?.role === 'admin' && (
                <Button onClick={() => navigate('/admin')} variant="ghost" size="sm"
                  data-testid="admin-settings-btn" aria-label="Admin settings" className="text-[#5A5A5A] hover:text-[#5C4023] hover:bg-[#F3EFE9]">
                  <Settings className="w-4 h-4" />
                  <span className="hidden sm:inline ml-2">Admin</span>
                </Button>
              )}
              <Button onClick={() => navigate('/import')} variant="ghost" size="sm"
                data-testid="import-btn" aria-label="Import resources" className="text-[#5A5A5A] hover:text-[#5C4023] hover:bg-[#F3EFE9]">
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline ml-2">Import</span>
              </Button>
              <Button onClick={handleExportCSV} variant="ghost" size="sm"
                data-testid="export-csv-btn" aria-label="Export resources as CSV" className="text-[#5A5A5A] hover:text-[#5C4023] hover:bg-[#F3EFE9]">
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline ml-2">Export</span>
              </Button>
              <Button onClick={logout} variant="ghost" size="sm"
                data-testid="logout-btn" aria-label="Sign out" className="text-[#5A5A5A] hover:text-[#E07A5F] hover:bg-[#F3EFE9]">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Search Bar + Filters */}
          <div className="mt-4 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#888888]" />
              <Input type="text" placeholder="Search resources..." data-testid="search-input" aria-label="Search resources"
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-xl border-[#E0D6C8] bg-white focus:ring-[#5C4023]/20 focus:border-[#5C4023] h-12" />
            </div>
            
            <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
              <SheetTrigger asChild>
                <Button data-testid="filter-btn"
                  className={`h-12 px-4 rounded-xl font-semibold transition-colors relative shrink-0 ${
                    activeFilterCount > 0
                      ? 'bg-[#5C4023] hover:bg-[#4A331C] text-white'
                      : 'bg-white border-2 border-[#5C4023] text-[#5C4023] hover:bg-[#5C4023]/5'
                  }`}>
                  <Filter className="w-5 h-5" />
                  <span className="ml-2">
                    {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : 'Filters'}
                  </span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
                <SheetHeader>
                  <SheetTitle className="font-['Manrope']">Filter Resources</SheetTitle>
                </SheetHeader>
                <ScrollArea className="flex-1 mt-4">
                  <div className="space-y-6 pr-4">
                    <div>
                      <label className="text-sm font-semibold text-[#1A1A1A] mb-3 block">SDOH Categories <span className="font-normal text-[#888]">(select multiple)</span></label>
                      <div className="grid grid-cols-1 gap-2">
                        {SDOH_CATEGORIES.map(cat => (
                          <label key={cat} htmlFor={`filter-cat-${cat}`}
                            className={`flex items-center space-x-3 rounded-lg px-3 py-2 cursor-pointer transition-colors ${
                              filters.sdoh_categories.includes(cat) ? 'bg-[#5C4023]/10' : 'hover:bg-[#F3EFE9]'
                            }`}>
                            <Checkbox id={`filter-cat-${cat}`} data-testid={`filter-category-${cat}`}
                              checked={filters.sdoh_categories.includes(cat)}
                              onCheckedChange={() => toggleArrayValue('sdoh_categories', cat)}
                              className="data-[state=checked]:bg-[#5C4023] data-[state=checked]:border-[#5C4023]" />
                            <span className="text-sm text-[#3A3A3A]">{cat}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-[#1A1A1A] mb-3 block">Neighborhood <span className="font-normal text-[#888]">(select multiple)</span></label>
                      <div className="flex flex-wrap gap-2">
                        {neighborhoods.map(n => (
                          <button key={n} type="button" data-testid={`filter-neighborhood-${n}`}
                            onClick={() => toggleArrayValue('neighborhoods', n)}
                            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                              filters.neighborhoods.includes(n)
                                ? 'bg-[#5C4023] text-white border-[#5C4023]'
                                : 'bg-white text-[#5A5A5A] border-[#E0D6C8] hover:border-[#5C4023]'
                            }`}>{n}</button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-[#1A1A1A] mb-3 block">Language <span className="font-normal text-[#888]">(select multiple)</span></label>
                      <div className="flex flex-wrap gap-2">
                        {FILTER_LANGUAGES.map(lng => (
                          <button key={lng} type="button" data-testid={`filter-language-${lng}`}
                            onClick={() => toggleArrayValue('languages', lng)}
                            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                              filters.languages.includes(lng)
                                ? 'bg-[#5C4023] text-white border-[#5C4023]'
                                : 'bg-white text-[#5A5A5A] border-[#E0D6C8] hover:border-[#5C4023]'
                            }`}>{lng}</button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-[#1A1A1A] mb-3 block">Cost <span className="font-normal text-[#888]">(select multiple)</span></label>
                      <div className="flex flex-wrap gap-2">
                        {COST_OPTIONS.map(c => (
                          <button key={c} type="button" data-testid={`filter-cost-${c}`}
                            onClick={() => toggleArrayValue('costs', c)}
                            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                              filters.costs.includes(c)
                                ? 'bg-[#5C4023] text-white border-[#5C4023]'
                                : 'bg-white text-[#5A5A5A] border-[#E0D6C8] hover:border-[#5C4023]'
                            }`}>{c}</button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-[#1A1A1A] mb-3 block">Service Type <span className="font-normal text-[#888]">(select multiple)</span></label>
                      <div className="flex flex-wrap gap-2">
                        {SERVICE_MODALITIES.map(m => (
                          <button key={m} type="button" data-testid={`filter-modality-${m}`}
                            onClick={() => toggleArrayValue('modalities', m)}
                            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                              filters.modalities.includes(m)
                                ? 'bg-[#5C4023] text-white border-[#5C4023]'
                                : 'bg-white text-[#5A5A5A] border-[#E0D6C8] hover:border-[#5C4023]'
                            }`}>{m}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                </ScrollArea>
                <div className="flex gap-3 pt-4 border-t border-[#E0D6C8]">
                  <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn" className="flex-1 rounded-full">Clear all filters</Button>
                  <Button onClick={() => setFilterOpen(false)} data-testid="apply-filters-btn" className="flex-1 bg-[#5C4023] hover:bg-[#4A331C] rounded-full">
                    Show results
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
            
            <Button onClick={() => navigate('/add')} data-testid="add-resource-btn"
              className="h-12 px-4 bg-[#5C4023] hover:bg-[#4A331C] rounded-xl shrink-0">
              <Plus className="w-5 h-5" /><span className="hidden sm:inline ml-2">Add</span>
            </Button>
          </div>
          
          {/* Active Filters */}
          {(activeFilterCount > 0 || searchQuery) && (
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {searchQuery && (
                <FilterChip testid="chip-search" label={`Search: ${searchQuery}`} onRemove={() => setSearchQuery('')} />
              )}
              {filters.sdoh_categories.map(cat => (
                <FilterChip key={`cat-${cat}`} testid={`chip-category-${cat}`} label={cat} onRemove={() => toggleArrayValue('sdoh_categories', cat)} />
              ))}
              {filters.neighborhoods.map(n => (
                <FilterChip key={`nb-${n}`} testid={`chip-neighborhood-${n}`} label={n} onRemove={() => toggleArrayValue('neighborhoods', n)} />
              ))}
              {filters.languages.map(l => (
                <FilterChip key={`lg-${l}`} testid={`chip-language-${l}`} label={l} onRemove={() => toggleArrayValue('languages', l)} />
              ))}
              {filters.costs.map(c => (
                <FilterChip key={`co-${c}`} testid={`chip-cost-${c}`} label={c} onRemove={() => toggleArrayValue('costs', c)} />
              ))}
              {filters.modalities.map(m => (
                <FilterChip key={`mo-${m}`} testid={`chip-modality-${m}`} label={m} onRemove={() => toggleArrayValue('modalities', m)} />
              ))}
              {(activeFilterCount > 0 || searchQuery) && (
                <button data-testid="clear-all-chips" onClick={clearFilters}
                  className="text-xs text-[#E07A5F] hover:underline font-medium ml-1">
                  Clear all
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <p className="text-sm text-[#5A5A5A]">
              {loading ? 'Loading...' : `${resources.length} resource${resources.length !== 1 ? 's' : ''} found`}
            </p>
            {resources.length > 0 && (
              <Button variant="ghost" size="sm" data-testid="toggle-selection-btn"
                onClick={() => { setSelectionMode(!selectionMode); if (selectionMode) setSelectedResources([]); }}
                className={`text-sm ${selectionMode ? 'text-[#5C4023] bg-[#5C4023]/10' : 'text-[#5A5A5A]'}`}>
                {selectionMode ? <CheckSquare className="w-4 h-4 mr-1" /> : <Square className="w-4 h-4 mr-1" />}
                {selectionMode ? 'Exit Selection' : 'Select for List'}
              </Button>
            )}
          </div>
          {selectionMode && resources.length > 0 && (
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSelectedResources(resources.map(r => r.id))} className="text-xs">Select All</Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedResources([])} className="text-xs">Clear</Button>
            </div>
          )}
          {!selectionMode && resources.length > 0 && (
            <div className="flex items-center gap-2">
              <ShareListButton
                shareUrl={shareUrl}
                lang={currentLanguage}
                label="Share"
                onShare={trackHandout}
                testid="share-results-btn"
                className="bg-white border-2 border-[#5C4023] text-[#5C4023] hover:bg-[#5C4023] hover:text-white rounded-full text-xs h-8 px-4"
              />
              <Button size="sm" onClick={handleCreateList} data-testid="create-list-results-btn"
                className="bg-[#E07A5F] hover:bg-[#D06045] text-white rounded-full text-xs h-8 px-4">
                <Printer className="w-4 h-4 mr-1.5" />Create Resource List
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <ReachLoader label="Finding resources…" />
        ) : loadError ? (
          <div className="empty-state" data-testid="load-error-state">
            <div className="w-20 h-20 rounded-full bg-[#F3EFE9] flex items-center justify-center mb-4">
              <X className="w-10 h-10 text-[#E07A5F]" />
            </div>
            <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">We couldn't load resources</h3>
            <p className="text-[#5A5A5A] mb-6">Check your connection, then try again.</p>
            <Button onClick={fetchResources} data-testid="retry-load-btn" className="bg-[#5C4023] hover:bg-[#4A331C] rounded-full">
              Try Again
            </Button>
          </div>
        ) : resources.length === 0 ? (
          <div className="empty-state">
            <div className="w-20 h-20 rounded-full bg-[#F3EFE9] flex items-center justify-center mb-4">
              <Heart className="w-10 h-10 text-[#E07A5F]" />
            </div>
            <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">No resources found</h3>
            <p className="text-[#5A5A5A] mb-6">Try adjusting your search or filters</p>
            {(searchQuery || Object.values(filters).some(f => f.length > 0)) ? (
              <Button onClick={() => { setSearchQuery(''); clearFilters(); }} data-testid="empty-clear-filters-btn" variant="outline" className="border-[#E0D6C8] text-[#5C4023] hover:bg-[#F3EFE9] rounded-full">
                <X className="w-4 h-4 mr-2" />Clear search & filters
              </Button>
            ) : (
              <Button onClick={() => navigate('/add')} className="bg-[#5C4023] hover:bg-[#4A331C] rounded-full">
                <Plus className="w-4 h-4 mr-2" />Add First Resource
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger-children">
            {resources.map((resource) => (
              <ResourceCard
                key={resource.id}
                resource={resource}
                isSelected={selectedResources.includes(resource.id)}
                selectionMode={selectionMode}
                onToggleSelection={toggleResourceSelection}
                onNavigate={navigate}
                getResourceName={getResourceName}
                getDisplayText={getDisplayText}
                freeLabel={freeLabel}
              />
            ))}
          </div>
        )}
      </main>

      <SelectionBar 
        count={selectedResources.length} 
        onPrint={handleCreateList} 
        onClear={() => { setSelectedResources([]); setSelectionMode(false); }} 
        extraAction={
          <ShareListButton
            shareUrl={shareUrl}
            lang={currentLanguage}
            label="Share"
            onShare={trackHandout}
            testid="share-selected-btn"
            className="bg-white/10 hover:bg-white/20 text-white rounded-full px-4 h-9 text-sm"
          />
        }
      />

      <LanguageBar 
        languages={SUPPORTED_LANGUAGES}
        currentLanguage={currentLanguage}
        onChange={handleLanguageChange}
      />
      
      <div className="h-16" />

      <OnboardingTour 
        isOpen={showTour} 
        onClose={() => setShowTour(false)} 
        isAdmin={user?.role === 'admin'} 
      />
    </div>
  );
};

export default FindResourcesPage;
